"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { Search, Heart, Star, Filter, MapPin, Building2 } from "lucide-react";
import Link from "next/link";
import { useI18n } from "@/i18n/I18nProvider";
import PageHeader from "@/components/layout/PageHeader";
import EmptyState from "@/components/ui/EmptyState";
import DemoNote from "@/components/ui/DemoNote";
import { getHospitals, cities, allSpecialties } from "@/services/mockService";
import { useFavorites } from "@/hooks/useFavorites";
import type { Hospital } from "@/data/mockData";

export default function HospitalsPage() {
  const { t } = useI18n();
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("All");
  const [specialty, setSpecialty] = useState("All");
  const [showFilters, setShowFilters] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();

  const queryKey = `${city}|${specialty}|${search}`;

  useEffect(() => {
    let alive = true;
    getHospitals({ city: city !== "All" ? city : undefined, specialty: specialty !== "All" ? specialty : undefined, search: search || undefined })
      .then((data) => {
        if (!alive) return;
        setHospitals(data);
        setLoadedFor(queryKey);
      });
    return () => { alive = false; };
  }, [city, specialty, search, queryKey]);

  const loading = loadedFor !== queryKey;

  const toggleFav = (id: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(id);
  };

  return (
    <div className="pb-24">
      <PageHeader
        title={t("Hospitals")}
        subtitle={t("Top-ranked medical centres across China")}
      >
        <div className="mt-3.5 flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-dark/35" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("Search hospitals or specialties...")}
              aria-label={t("Search hospitals or specialties...")}
              className="cc-input pl-11"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            aria-label={t("Toggle filters")}
            className={`w-12 h-12 shrink-0 rounded-[14px] border flex items-center justify-center transition-colors ${
              showFilters
                ? "bg-primary border-primary text-white"
                : "bg-white border-[#E3E9E9] text-text-dark/60"
            }`}
          >
            <Filter size={18} />
          </button>
        </div>
      </PageHeader>

      <DemoNote text={t("Demo hospital notice")} />

      {/* Filters */}
      {showFilters && (
        <div className="bg-white border-b border-[#EDF1F1] px-5 py-3 space-y-3">
          <div>
            <p className="cc-label mb-2 text-text-dark/70">{t("City")}</p>
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {cities.map((c) => (
                <button key={c} onClick={() => setCity(c)}
                  className={`cc-chip ${city === c ? "cc-chip-active" : ""}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="cc-label mb-2 text-text-dark/70">{t("Specialty")}</p>
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {allSpecialties.map((s) => (
                <button key={s} onClick={() => setSpecialty(s)}
                  className={`cc-chip ${specialty === s ? "cc-chip-active" : ""}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Hospital list */}
      <div className="px-5 py-4 space-y-3">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <div key={i} className="cc-skeleton h-[236px]" />)
        ) : hospitals.length === 0 ? (
          <EmptyState
            icon={Building2}
            title={t("No hospitals found")}
            hint={t("Try another city, specialty or keyword.")}
          />
        ) : (
          hospitals.map((hospital) => (
            <Link key={hospital.id} href={`/hospitals/${hospital.id}`} className="block">
              <div className="cc-card overflow-hidden active:scale-[0.98] transition-transform">
                <div className="relative h-[150px] bg-primary-bg">
                  <Image
                    src={hospital.image}
                    alt={hospital.name}
                    fill
                    sizes="(max-width: 430px) 100vw, 430px"
                    className="object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/25 to-transparent" />
                  <button onClick={(e) => toggleFav(hospital.id, e)}
                    aria-label={isFavorite(hospital.id) ? t("Remove from favorites") : t("Add to favorites")}
                    className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow-sm backdrop-blur-sm">
                    <Heart size={15} className={isFavorite(hospital.id) ? "fill-red-500 text-red-500" : "text-text-dark/40"} />
                  </button>
                  {hospital.isTop && (
                    <span className="absolute top-2.5 left-2.5 bg-amber-400 text-[11px] font-bold text-white px-2 py-1 rounded-full shadow-sm">{t("Top Hospital")}</span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-[15.5px] font-bold leading-[1.3] text-text-dark">{hospital.name}</h3>
                  <div className="mt-1.5 flex items-center gap-1">
                    <MapPin size={13} className="text-text-dark/40" />
                    <span className="text-[13px] text-text-dark/55">{hospital.city}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {hospital.specialties.slice(0, 3).map((s) => (
                      <span key={s} className="text-[12px] bg-primary-bg text-primary px-2 py-0.5 rounded-lg font-medium">{s}</span>
                    ))}
                  </div>
                  <div className="mt-2.5 flex items-center gap-1">
                    <Star size={13} className="fill-amber-400 text-amber-400" />
                    <span className="text-[13px] font-semibold text-text-dark">{hospital.rating}</span>
                    <span className="text-[12px] text-text-dark/45">({hospital.reviews} {t("reviews")})</span>
                  </div>
                </div>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
